import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tooltip } from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Plus, Edit2, Copy, Trash2, Lock, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { GET_ROLES, DELETE_ROLE_MUTATION } from '../graphql/queries';
import { RoleDialog, RoleDialogRole } from '../components/role/RoleDialog';

interface RoleRow {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  isOwnerRole: boolean;
  permissions: string[];
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

export const RolesPage: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{ roles: RoleRow[] }>(GET_ROLES, {
    fetchPolicy: 'cache-and-network',
  });

  const [deleteRole, { loading: deleting }] = useMutation(DELETE_ROLE_MUTATION, {
    refetchQueries: [{ query: GET_ROLES }],
    awaitRefetchQueries: true,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'clone'>('create');
  const [activeRole, setActiveRole] = useState<RoleDialogRole | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<RoleRow | null>(null);

  const roles = data?.roles || [];

  const handleCreate = () => {
    setDialogMode('create');
    setActiveRole(null);
    setDialogOpen(true);
  };

  const handleEdit = (role: RoleRow) => {
    setDialogMode('edit');
    setActiveRole(role);
    setDialogOpen(true);
  };

  const handleClone = (role: RoleRow) => {
    setDialogMode('clone');
    setActiveRole(role);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteRole({ variables: { id: confirmDelete.id } });
      toast.success(`Rola "${confirmDelete.name}" została usunięta`);
      setConfirmDelete(null);
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć roli');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Role i uprawnienia
          </h2>
          <p className="text-muted-foreground mt-2">
            Zarządzaj rolami systemowymi i niestandardowymi oraz przypisanymi do nich uprawnieniami
          </p>
        </div>
        <Button onClick={handleCreate} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Nowa rola</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista ról</CardTitle>
          <CardDescription>
            Role systemowe nie mogą być edytowane ani usuwane. Kliknij "Klonuj" aby utworzyć rolę bazującą na istniejącej.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && roles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Ładowanie ról...</p>
          ) : error ? (
            <p className="text-sm text-red-600 py-4">Błąd: {error.message}</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Brak zdefiniowanych ról.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-2 font-medium">Nazwa</th>
                    <th className="py-2 px-2 font-medium">Opis</th>
                    <th className="py-2 px-2 font-medium text-center">Uprawnienia</th>
                    <th className="py-2 px-2 font-medium text-center">Pracownicy</th>
                    <th className="py-2 px-2 font-medium text-right">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => {
                    const cannotDelete = role.isSystem || role.employeeCount > 0;
                    const deleteReason = role.isSystem
                      ? 'Rola systemowa nie może być usunięta'
                      : role.employeeCount > 0
                      ? `Rola jest przypisana do ${role.employeeCount} pracowników`
                      : '';
                    return (
                      <tr key={role.id} className="border-b hover:bg-accent/20">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{role.name}</span>
                            {role.isSystem && (
                              <Tooltip content="Rola systemowa — niemodyfikowalna" side="top">
                                <Lock className="w-3.5 h-3.5 text-blue-600 cursor-help" />
                              </Tooltip>
                            )}
                            {role.isOwnerRole && (
                              <Tooltip content="Rola właściciela systemu" side="top">
                                <Shield className="w-3.5 h-3.5 text-amber-500 cursor-help" />
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">
                          {role.description || <span className="italic">brak opisu</span>}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline">{role.permissions.length}</Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline">{role.employeeCount}</Badge>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex justify-end gap-1">
                            <Tooltip content={role.isSystem ? 'Podgląd roli systemowej' : 'Edytuj rolę'} side="top">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(role)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Klonuj rolę" side="top">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleClone(role)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                            {cannotDelete ? (
                              <Tooltip content={deleteReason} side="top">
                                <Button variant="outline" size="sm" disabled>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip content="Usuń rolę" side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setConfirmDelete(role)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RoleDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          // small delay so refetch finishes — refetchQueries already handles this
          refetch().catch(() => {});
        }}
        mode={dialogMode}
        role={activeRole}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Usuń rolę
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć rolę <strong>{confirmDelete?.name}</strong>?
              Tej operacji nie można cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
